import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Collapsible } from 'ncoded-component-library';
import { Field, useForm, useFormState } from 'react-final-form';
import get from 'lodash.get';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import {
  updateViewOrDownloadForMember,
  updateFromAllCheckboxValue,
} from '../../checkboxUtils';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import { FieldArray } from 'react-final-form-arrays';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { BulkFormAvailabilityMember } from 'router/subrouters/DataRoom/models/DataRoomBulk';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import DurationDaysField from 'components/DurationDaysField';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  getFileExpirationDateInDays,
  getIsCheckedAndNotMixed,
} from '../../utils';
import bus, { StandardEventTypes } from 'modules/bus';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useGetExpirationDate, {
  MIN_EXPIRATION_DAYS,
} from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/hooks/useGetExpirationDate';
import { addDaysToDate, getIsArrayEmpty, isSingleElementArray } from 'utils';

import './GroupAccess.styles.scss';

type GroupAccessProps = {
  className?: string;
  name: string;
  title: string;
  hasFileAccess?: boolean;
  isSingleMemberGroup?: boolean;
};

const GroupAccess: React.ForwardRefRenderFunction<
  CollapsibleRef,
  GroupAccessProps
> = (props, ref) => {
  const {
    className,
    name,
    title,
    isSingleMemberGroup = false,
    hasFileAccess = true,
  } = props;

  const classes = classNames('group-access', className);

  const [collapse, collapseRef] = useCallbackRef<CollapsibleRef>();

  const {
    dataRoom: { accessConfirmation },
  } = useContext(DataRoomContext);

  useImperativeHandle(ref, () => collapse, [collapse]);

  const { batch, change } = useForm();
  const { values } = useFormState();

  const { t } = useTranslation();

  const getExpirationDate = useGetExpirationDate();

  const members = useMemo(() => get(values, name) || [], [values, name]);

  const isSingle = useMemo(() => isSingleElementArray(members), [members]);

  const showFileAccess = useMemo(
    () => hasFileAccess && accessConfirmation,
    [accessConfirmation, hasFileAccess],
  );

  const leadName = useMemo(
    () =>
      members.find((member: BulkFormAvailabilityMember) => member.isLead)?.name,
    [members],
  );

  const showLeadName = useMemo(
    () => !isSingleMemberGroup && !isSingle && leadName,
    [isSingle, isSingleMemberGroup, leadName],
  );

  const isViewNotCheckedForAll = useMemo(
    () =>
      members.every(
        (member: BulkFormAvailabilityMember) => !member.view.checked,
      ),
    [members],
  );

  const isDownloadNotCheckedForAll = useMemo(
    () =>
      members.every(
        (member: BulkFormAvailabilityMember) => !member.download.checked,
      ),
    [members],
  );

  const isViewMixedForAll = useMemo(
    () =>
      !isViewNotCheckedForAll &&
      !members.every((member: BulkFormAvailabilityMember) =>
        getIsCheckedAndNotMixed(member.view),
      ),
    [isViewNotCheckedForAll, members],
  );

  const isDownloadMixedForAll = useMemo(
    () =>
      !isDownloadNotCheckedForAll &&
      !members.every(
        (member: BulkFormAvailabilityMember) =>
          member.download.checked && !member.download.isMixed,
      ),
    [isDownloadNotCheckedForAll, members],
  );

  const broadcastAccessCycleEvent = useCallback(
    () =>
      setTimeout(
        () => bus.broadcastEvent(StandardEventTypes.ACCESS_CYCLE_EVENT),
        0,
      ),
    [],
  );

  const updateViewForAll = useCallback(() => {
    batch(() =>
      members.forEach((member: BulkFormAvailabilityMember, index: number) =>
        updateFromAllCheckboxValue({
          name: `${name}[${index}]`,
          isDownload: false,
          checked: isViewNotCheckedForAll || isViewMixedForAll,
          memberCurrentState: member,
          onChange: change,
        }),
      ),
    );

    broadcastAccessCycleEvent();
  }, [
    name,
    members,
    isViewNotCheckedForAll,
    isViewMixedForAll,
    change,
    batch,
    broadcastAccessCycleEvent,
  ]);

  const updateDownloadForAll = useCallback(() => {
    batch(() =>
      members.forEach((member: BulkFormAvailabilityMember, index: number) =>
        updateFromAllCheckboxValue({
          name: `${name}[${index}]`,
          isDownload: true,
          checked: isDownloadNotCheckedForAll || isDownloadMixedForAll,
          memberCurrentState: member,
          onChange: change,
        }),
      ),
    );

    broadcastAccessCycleEvent();
  }, [
    name,
    members,
    isDownloadNotCheckedForAll,
    isDownloadMixedForAll,
    change,
    batch,
    broadcastAccessCycleEvent,
  ]);

  const getExpirationDateTooltip = useCallback(
    (filesAccessExpiredAt: Date, fileExpirationDateInDays: number) => {
      const oldValue = getFileExpirationDateInDays(filesAccessExpiredAt);

      if (oldValue === fileExpirationDateInDays)
        return getExpirationDate(filesAccessExpiredAt);

      return getExpirationDate(addDaysToDate(fileExpirationDateInDays));
    },
    [getExpirationDate],
  );

  const trigger = useMemo(() => {
    return (
      <div>
        <div className="heading">
          <p className="name">
            {title}
            {!isSingle && (
              <Counter
                count={members?.length}
                variant={COUNTER_VARIANTS.GREY}
              />
            )}
          </p>
          {showLeadName && <p className="lead">{leadName}</p>}
          <Button icon={ChevrondownIcon} variant="icon" />
        </div>

        <div className="subheading">
          <p>{t('name')}</p>

          <div>
            {showFileAccess && <p className="cycle">{t('accessCycle')}</p>}

            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                label={t('view')}
                checked={!isViewNotCheckedForAll}
                className={classNames({ mixed: isViewMixedForAll })}
                onChange={updateViewForAll}
              />
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                label={t('download')}
                checked={!isDownloadNotCheckedForAll}
                className={classNames({ mixed: isDownloadMixedForAll })}
                onChange={updateDownloadForAll}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    isSingle,
    leadName,
    showLeadName,
    title,
    members?.length,
    isViewNotCheckedForAll,
    isViewMixedForAll,
    isDownloadNotCheckedForAll,
    isDownloadMixedForAll,
    showFileAccess,
    updateViewForAll,
    updateDownloadForAll,
    t,
  ]);

  if (getIsArrayEmpty(members)) return null;

  return (
    <Collapsible
      defaultOpen
      className={classes}
      trigger={trigger}
      ref={collapseRef}
    >
      <FieldArray name={name}>
        {({ fields }) => (
          <>
            {fields.map((name, index) => {
              const memberCurrentState = fields.value[index];

              const showMemberFileAccess = getIsCheckedAndNotMixed(
                memberCurrentState.view,
              );

              const inDays = +memberCurrentState.fileExpirationDateInDays;

              return (
                <div key={index}>
                  <p>{memberCurrentState.name}</p>

                  <div className="actions">
                    {showFileAccess && showMemberFileAccess && (
                      <>
                        <DurationDaysField
                          name={`${name}.fileExpirationDateInDays`}
                          label={t('in')}
                          required
                          disabled={memberCurrentState.view.disabled}
                          copyForMinValidation="minimumAccessCycleInDays"
                          copyForMaxValidation="maximumAccessCycleInDays"
                          customOnBlur={broadcastAccessCycleEvent}
                        />
                        {inDays > 0 && inDays <= MIN_EXPIRATION_DAYS && (
                          <ResolvePopup
                            onHover
                            popupContent={getExpirationDateTooltip(
                              memberCurrentState.filesAccessExpiredAt,
                              inDays,
                            )}
                          >
                            <InformationcircleIcon />
                          </ResolvePopup>
                        )}
                      </>
                    )}

                    <div className="checkboxes">
                      <Field
                        name={`${name}.view.checked`}
                        component={CheckboxField}
                        type="checkbox"
                        disabled={memberCurrentState.view.disabled}
                        className={classNames({
                          mixed: memberCurrentState.view.isMixed,
                        })}
                        onChange={(checked: boolean) => {
                          updateViewOrDownloadForMember({
                            name,
                            checked,
                            isDownload: false,
                            memberCurrentState,
                            onChange: change,
                          });
                          broadcastAccessCycleEvent();
                        }}
                      />

                      <Field
                        name={`${name}.download.checked`}
                        component={CheckboxField}
                        type="checkbox"
                        disabled={memberCurrentState.download.disabled}
                        className={classNames({
                          mixed: memberCurrentState.download.isMixed,
                        })}
                        onChange={(checked: boolean) => {
                          updateViewOrDownloadForMember({
                            name,
                            checked,
                            isDownload: true,
                            memberCurrentState,
                            onChange: change,
                          });
                          broadcastAccessCycleEvent();
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </FieldArray>
    </Collapsible>
  );
};

export default forwardRef(GroupAccess);
